import styles from './courses.module.scss';

import { Box, Container, CreateHeaders, dateHeader, HeaderSizeEnum, Icon, OrderTypesEnum, PrimaryButton, textHeader, Tab, Tabs, currencyHeader, booleanHeader, FocusTable, ErrorButton } from '@frontend-workspace/react-shared-lib';
import { useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { UseSignalRHook } from '../signalr-context/signalr-context';
import ChangeCourseUsersPasswordDialog from '../change-course-users-password-dialog/change-course-users-password-dialog';
import PageTitle from '../page-title/page-title';
import WebkuaTable from '../webkua-table/webkua-table';
import FullTable from '../full-table/full-table';
import { ValueFormatterParams } from 'ag-grid-community';
import { addYears } from 'date-fns';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface CoursesProps { }

export function Courses(props: CoursesProps) {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <PageTitle title={t('app.course')} />
            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
              <Tabs
                value={selectedTab}
                variant="scrollable"
                scrollButtons="auto">
                <Tab label={t('courses.courseAccess')} onClick={navigateToTabOne} />
                <Tab label={t('standard.courseList')} onClick={navigateToTabTwo} />
              </Tabs>
            </Box>
            {selectedTab === 0 && <CourseAccess />}
            {selectedTab === 1 && <CourseList />}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}
function CourseAccess() {
  const makeRequest = useMakeRequestHook();

  const { t } = useTranslation();
  const { data, getData } = useGetRequestNewHook('courses/courseEmails/onlyActive/true', [], []);
  UseSignalRHook(getData, [OrderTypesEnum.InitializeCourseOrder]);

  const deleteOrderRenderer = (params: ValueFormatterParams) => {
    const handleDelete = () => makeRequest(`courses/action/deleteCoursePassword/${params.value}`, 'DELETE').then(result => {
      getData();
    });
    return <ErrorButton
      variant='outlined'
      size='small'
      onClick={handleDelete}>
      {t('standard.delete')}
    </ErrorButton>
  }
  const headers = CreateHeaders([
    textHeader("email", t('standard.email'), HeaderSizeEnum.MEDIUM),
    textHeader("firstName", t('standard.firstName'), HeaderSizeEnum.SMALL),
    textHeader("lastName", t('standard.lastName'), HeaderSizeEnum.SMALL),
    textHeader("courseName", t('standard.courseName'), HeaderSizeEnum.EXTRA_LARGE),
    dateHeader("usedFrom", t('courses.usedFrom')),
    dateHeader("usedUntil", t('courses.usedUntil')),
    textHeader("password", t('courses.passwordForCourse'), HeaderSizeEnum.LARGE),
    textHeader("orderId", "", HeaderSizeEnum.SMALL, {
      renderer: deleteOrderRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);

  const [isNewCourseOpen, setIsNewCourseOpen] = useState(false);
  const toggleNewCourseOpen = () => setIsNewCourseOpen(!isNewCourseOpen);

  return <>
    <PrimaryButton
      variant='contained'
      onClick={toggleNewCourseOpen}>
      <Icon className="me-1" fontSize="small">add</Icon>{t('courses.createNewCourse')}
    </PrimaryButton>
    <WebkuaTable
      headers={headers}
      data={data}
    />
    {isNewCourseOpen && <ChangeCourseUsersPasswordDialog
      onClose={toggleNewCourseOpen}
    />}
  </>
}
function CourseList() {
  const { t } = useTranslation();
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const headers = CreateHeaders([
    textHeader("courseName", t('standard.course'), HeaderSizeEnum.EXTRA_LARGE, {
      renderer: "agGroupCellRenderer",
      cellStyle: { fontSize: '0.875rem', fontWeight: '600' }
    }),
    dateHeader("date", t('standard.date'), { sort: "desc" }),
    booleanHeader("isCanceled", t('standard.cancelled')),
    textHeader("instructorName", t('standard.instructor'), HeaderSizeEnum.LARGE),
    textHeader("numberOfDays", t('standard.numberOfDays'), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("city", "Sted", HeaderSizeEnum.MEDIUM),
    textHeader("numberOfParticipants", t('courses.numberOfParticipants'), HeaderSizeEnum.SMALL, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("coursePrice", t('courses.coursePrice'), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("sum", "Sum", HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);
  return (
    <FullTable
      headers={headers}
      supportFutureDates={true}
      url={`courses/includeCancelled/${includeCancelled}`}
      tableConfigName={"courses"}
      initialStartDate={new Date()}
      initialEndDate={addYears(new Date(), 1)}
      pageSize={15}
      disableProductsFilter
      ignoreCompany
      disableGroupFilter
      expandComponent={Registrations}
      tableFilters={[{
        label: t('courses.includeCanceledCourses'),
        checked: includeCancelled,
        onChange: setIncludeCancelled
      }]}
    />
  )
}
function Registrations(props: any) {
  const { t } = useTranslation();
  return (
    <Box className="py-4 px-2">
      <FocusTable
        data={props.data.registrations}
        headers={CreateHeaders([
          textHeader("person.firstName", t('standard.firstName'), HeaderSizeEnum.LARGE),
          textHeader("person.lastName", t('standard.lastName'), HeaderSizeEnum.LARGE),
          textHeader("person.email", t('standard.email'), HeaderSizeEnum.EXTRA_LARGE),
          textHeader("person.phoneNumber", t('standard.phone'), HeaderSizeEnum.MEDIUM),
          textHeader("person.companyName", t('standard.company'), HeaderSizeEnum.LARGE),
          booleanHeader("hasAnsweredSurvey", t('courses.answeredSurvey')),
          booleanHeader("hasSentCertificate", t('courses.sentCertificate')),
          booleanHeader("canceled", t('standard.cancelled'))
        ])}
      />
    </Box>
  )
}

export default Courses;